/** Statistic used to reduce a day's samples into one value at sync time. */
export type DayAggregateStatistic = 'average' | 'min' | 'max' | 'last' | 'sum';

/** One timed sample used by day aggregation. */
export type DaySample = {
  readonly value: number;
  readonly timestamp: string;
};

/**
 * Reduces a day's samples to a single value using the given statistic.
 * `last` picks the sample with the latest timestamp (not array order).
 * @param samples Timed samples for one local day
 * @param statistic Aggregation to apply
 * @returns Aggregated value, or null when there are no samples
 */
export function aggregateDaySamples(
  samples: readonly DaySample[],
  statistic: DayAggregateStatistic,
): number | null {
  if (samples.length === 0) {
    return null;
  }

  if (statistic === 'last') {
    let latest = samples[0]!;
    for (let index = 1; index < samples.length; index += 1) {
      const sample = samples[index]!;
      if (sample.timestamp > latest.timestamp) {
        latest = sample;
      }
    }
    return latest.value;
  }

  if (statistic === 'min') {
    let minimum = samples[0]!.value;
    for (let index = 1; index < samples.length; index += 1) {
      const value = samples[index]!.value;
      if (value < minimum) {
        minimum = value;
      }
    }
    return minimum;
  }

  if (statistic === 'max') {
    let maximum = samples[0]!.value;
    for (let index = 1; index < samples.length; index += 1) {
      const value = samples[index]!.value;
      if (value > maximum) {
        maximum = value;
      }
    }
    return maximum;
  }

  if (statistic === 'sum') {
    let total = 0;
    for (const sample of samples) {
      total += sample.value;
    }
    return total;
  }

  let total = 0;
  for (const sample of samples) {
    total += sample.value;
  }
  return total / samples.length;
}
